import { namespace, Point } from '../../../../../../FrontendBundle/Resources/js/ofcs';
import AbstractResizeHandle from './abstractResizeHandle';
import ResizeHandleMarker from './resizeHandleMarker';

/**
 * Represents the resize handle on a svg canvas for a circle.
 *
 * @class   Ofcs.Editor.Svg.Handle.ResizeCircleHandle
 * @extends Ofcs.Editor.Svg.Handle.AbstractResizeHandle
 */
export default class ResizeCircleHandle extends AbstractResizeHandle {
    /**
     * Initialize drag handles.
     *
     * @protected
     *
     * @param {Ofcs.Editor.Item.CircleItem} circle
     */
    initializePointers() {
        this.handles = {
            topCenter: new ResizeHandleMarker('topCenter', 'bottomCenter', this.createPointerElement(), 0, -1),
            centerLeft: new ResizeHandleMarker('centerLeft', 'centerRight', this.createPointerElement(), -1, 0),
            centerRight: new ResizeHandleMarker('centerRight', 'centerLeft', this.createPointerElement(), 1, 0),
            bottomCenter: new ResizeHandleMarker('bottomCenter', 'topCenter', this.createPointerElement(), 0, 1),
        };
    }

    initializeCommand() {
        this.command = new Ofcs.Editor.Command.ResizeItemCommand(this.item);
    }

    configureResizeCommand(marker, newAbsoluteMarkerPosition) {
        const size = this.calculateNewSize(marker, newAbsoluteMarkerPosition);

        if (this.item.validate(size) !== null) return false;

        const newPosition = this.calculateNewPosition(marker, size);

        this.command.setResizeData(size.width, size.height, newPosition);

        return true;
    }

    calculateNewSize(marker, newAbsoluteMarkerPosition) {
        const absolutePoints = this.calculateHandlePointsForPosition(this.item.getAbsolutePosition());
        const oppositePosition = absolutePoints[marker.oppositeKey];

        let diameter = 0;
        if (marker.multiplierX) diameter = (newAbsoluteMarkerPosition.x - oppositePosition.x) * marker.multiplierX;
        else diameter = (newAbsoluteMarkerPosition.y - oppositePosition.y) * marker.multiplierY;

        return {
            width: diameter,
            height: diameter,
        };
    }

    calculateNewPosition(marker, size) {
        const relativePoints = this.calculateHandlePointsForPosition(this.item.get('position'));
        const oppositeHandlePosition = relativePoints[marker.oppositeKey];
        const normalizedOppositeHandlePosition = this.getNormalizedPoint(oppositeHandlePosition);

        const newCenter = new Point(
            normalizedOppositeHandlePosition.x + (marker.multiplierX * (size.width / 2)),
            normalizedOppositeHandlePosition.y + (marker.multiplierY * (size.height / 2)),
        );

        // convert normalized center position to real (rotated) position
        return this.getTransformedPoint(newCenter);
    }

    /**
     * Calculate diameter using two points and x-axis / y-axis flags.
     *
     * @protected
     *
     * @param {Point} opposite The opposite point of the dragged handle.
     * @param {Point} current The new position of the moved resize handle after dragging.
     * @param {number} multiplierX 0 = ignore x movement, -1 = invert x movement, 1 = process x movement
     * @param {number} multiplierY 0 = ignore y movement, -1 = invert y movement, 1 = process y movement
     *
     * @return {number} diameter
     */
    calculateDiameter(opposite, current, multiplierX, multiplierY) {
        let diameter;

        if (multiplierX) {
            // movement on x-axis (left or right handle is dragged)
            diameter = (current.x - opposite.x) * multiplierX;
        } else {
            // movement on y-axis (left or right handle is dragged)
            diameter = (current.y - opposite.y) * multiplierY;
        }

        return diameter;
    }
}

namespace('Ofcs.Editor.Svg.Handle');
window.Ofcs.Editor.Svg.Handle.ResizeCircleHandle = ResizeCircleHandle;
